/**
 * THE CHI ALLIANCE — EVENT DISCOVERY REVIEW CANVASES
 * Design reminder: three brand-guide-aligned enterprise directions; black and deep evergreen ground the UI,
 * while leaf green signals selection, gold indicates featured priority, and magenta supplies human energy.
 */
import { useMemo, useState, type CSSProperties } from "react";
import { Link, useLocation } from "wouter";
import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Filter,
  MapPin,
  MoveRight,
  Sparkles,
  UserRound,
  X,
} from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CtaIcon from "@/components/CtaIcon";
import { EVENTS, type CHIEvent } from "@/lib/eventsData";

type CanvasKey = "signal-room" | "program-ledger" | "network-atlas";

type CanvasMeta = {
  key: CanvasKey;
  number: string;
  name: string;
  label: string;
  description: string;
};

const CANVASES: CanvasMeta[] = [
  {
    key: "signal-room",
    number: "01",
    name: "Signal Room",
    label: "Editorial keynote",
    description: "An intimate, cinematic headline moment with a persistent program rail.",
  },
  {
    key: "program-ledger",
    number: "02",
    name: "Program Ledger",
    label: "Executive control plane",
    description: "A concise program desk that makes a growing calendar feel clear and operational.",
  },
  {
    key: "network-atlas",
    number: "03",
    name: "Network Atlas",
    label: "Relationship discovery",
    description: "A more immersive browse experience that favors people, places, and topical pathways.",
  },
];

const TOPIC_BY_EVENT: Record<number, string> = {
  1: "People Strategy",
  2: "HR Technology & AI",
  3: "Talent & Growth",
  4: "Future of Work",
  5: "Leadership Communication",
  6: "Decision Making",
  7: "Community",
  8: "Leadership Growth",
  9: "Community & AI",
};

const FEATURED_EVENT = EVENTS[0];

function canvasFromPath(pathname: string): CanvasKey {
  if (pathname.endsWith("program-ledger")) return "program-ledger";
  if (pathname.endsWith("network-atlas")) return "network-atlas";
  return "signal-room";
}

function locationGroup(event: CHIEvent) {
  return event.type === "virtual" ? "Virtual" : "Dallas";
}

function eventTopic(event: CHIEvent) {
  return TOPIC_BY_EVENT[event.id] ?? "Community";
}

function dateBits(event: CHIEvent) {
  const [month = "TBD", date = ""] = event.displayDate.split(" ");
  return { month: month.slice(0, 3), date: date.replace(/[^0-9]/g, "") || "—" };
}

function eventFormat(event: CHIEvent) {
  return event.type === "virtual" ? "Virtual session" : "In-person gathering";
}

type FilterControlsProps = {
  locationFilter: string;
  setLocationFilter: (value: string) => void;
  topicFilter: string;
  setTopicFilter: (value: string) => void;
  speakerFilter: string;
  setSpeakerFilter: (value: string) => void;
  onClear: () => void;
  resultCount: number;
  compact?: boolean;
};

function FilterControls({
  locationFilter,
  setLocationFilter,
  topicFilter,
  setTopicFilter,
  speakerFilter,
  setSpeakerFilter,
  onClear,
  resultCount,
  compact = false,
}: FilterControlsProps) {
  const locations = ["all", "Dallas", "Virtual"];
  const topics = ["all", ...Array.from(new Set(EVENTS.map(eventTopic)))];
  const speakers = ["all", ...Array.from(new Set(EVENTS.map((event) => event.speaker)))];
  const hasFilters = locationFilter !== "all" || topicFilter !== "all" || speakerFilter !== "all";

  return (
    <section className={`canvas-filter-panel${compact ? " canvas-filter-panel--compact" : ""}`} aria-label="Refine events">
      <div className="canvas-filter-panel__intro">
        <span className="canvas-filter-panel__eyebrow"><Filter size={14} aria-hidden="true" /> Refine the program</span>
        <p aria-live="polite">{resultCount} {resultCount === 1 ? "session" : "sessions"} in view</p>
      </div>
      <div className="canvas-filter-panel__controls">
        <label>
          <span>Location</span>
          <div className="canvas-select-shell">
            <select value={locationFilter} onChange={(event) => setLocationFilter(event.target.value)}>
              {locations.map((location) => <option key={location} value={location}>{location === "all" ? "Every location" : location}</option>)}
            </select>
            <ChevronDown size={15} aria-hidden="true" />
          </div>
        </label>
        <label>
          <span>Topic</span>
          <div className="canvas-select-shell">
            <select value={topicFilter} onChange={(event) => setTopicFilter(event.target.value)}>
              {topics.map((topic) => <option key={topic} value={topic}>{topic === "all" ? "Every topic" : topic}</option>)}
            </select>
            <ChevronDown size={15} aria-hidden="true" />
          </div>
        </label>
        <label>
          <span>Speaker</span>
          <div className="canvas-select-shell">
            <select value={speakerFilter} onChange={(event) => setSpeakerFilter(event.target.value)}>
              {speakers.map((speaker) => <option key={speaker} value={speaker}>{speaker === "all" ? "Every speaker" : speaker}</option>)}
            </select>
            <ChevronDown size={15} aria-hidden="true" />
          </div>
        </label>
      </div>
      <button type="button" className="canvas-filter-panel__clear" onClick={onClear} disabled={!hasFilters}>
        <X size={14} aria-hidden="true" /> Clear filters
      </button>
    </section>
  );
}

function DateStamp({ event, variant = "standard" }: { event: CHIEvent; variant?: "standard" | "ledger" | "atlas" }) {
  const { month, date } = dateBits(event);
  return (
    <div className={`event-date-stamp event-date-stamp--${variant}`} aria-label={event.displayDate}>
      <span>{month}</span>
      <strong>{date}</strong>
    </div>
  );
}

function EventMeta({ event }: { event: CHIEvent }) {
  return (
    <div className="canvas-event-meta">
      <span><Clock3 size={14} aria-hidden="true" /> {event.time}</span>
      <span><MapPin size={14} aria-hidden="true" /> {locationGroup(event)}</span>
      <span><UserRound size={14} aria-hidden="true" /> {event.speaker}</span>
    </div>
  );
}

function EmptyProgram() {
  return (
    <div className="canvas-empty-state">
      <Sparkles size={24} aria-hidden="true" />
      <strong>No sessions match this view yet.</strong>
      <p>Clear one or more filters to explore the full CHI Alliance program.</p>
    </div>
  );
}

function ProgramList({ events }: { events: CHIEvent[] }) {
  if (!events.length) return <EmptyProgram />;
  return (
    <div className="signal-program-list">
      {events.map((event) => (
        <article className="signal-program-card" key={event.id} style={{ "--event-tone": event.color } as CSSProperties}>
          <DateStamp event={event} />
          <div className="signal-program-card__main">
            <div className="signal-program-card__labels"><span>{eventFormat(event)}</span><span>{eventTopic(event)}</span></div>
            <h3>{event.title}</h3>
            <EventMeta event={event} />
          </div>
          <Link href={`/events/${event.slug}`} className="signal-program-card__link">Session details <CtaIcon icon={CalendarDays} size={14} /></Link>
        </article>
      ))}
    </div>
  );
}

function LedgerList({ events }: { events: CHIEvent[] }) {
  if (!events.length) return <EmptyProgram />;
  return (
    <div className="ledger-list">
      {events.map((event, index) => (
        <article className="ledger-row" key={event.id} style={{ "--event-tone": event.color } as CSSProperties}>
          <span className="ledger-row__index">{String(index + 1).padStart(2, "0")}</span>
          <DateStamp event={event} variant="ledger" />
          <div className="ledger-row__session">
            <p>{eventTopic(event)}</p>
            <h3>{event.title}</h3>
            <span>{event.subtitle || event.speakerOrg}</span>
          </div>
          <div className="ledger-row__speaker"><span>Hosted by</span><strong>{event.speaker}</strong></div>
          <div className="ledger-row__where"><span>{eventFormat(event)}</span><strong>{locationGroup(event)}</strong></div>
          <Link href={`/events/${event.slug}`} className="ledger-row__link" aria-label={`Open ${event.title}`}><MoveRight size={20} aria-hidden="true" /></Link>
        </article>
      ))}
    </div>
  );
}

function AtlasGrid({ events }: { events: CHIEvent[] }) {
  if (!events.length) return <EmptyProgram />;
  return (
    <div className="atlas-grid">
      {events.map((event, index) => {
        const image = event.portraitImage || event.heroImage || (index % 2 === 0 ? "/media/events/salon-networking.webp" : "/media/events/featured-session-stage.webp");
        return (
          <article className="atlas-card" key={event.id} style={{ "--event-tone": event.color } as CSSProperties}>
            <div className="atlas-card__image">
              <img src={image} alt="" aria-hidden="true" />
              <div className="atlas-card__image-veil" />
              <DateStamp event={event} variant="atlas" />
              <span className="atlas-card__topic">{eventTopic(event)}</span>
            </div>
            <div className="atlas-card__body">
              <p>{eventFormat(event)} · {locationGroup(event)}</p>
              <h3>{event.title}</h3>
              <div className="atlas-card__speaker"><UserRound size={15} aria-hidden="true" /> {event.speaker}</div>
              <Link href={`/events/${event.slug}`} className="atlas-card__link">Explore the session <CtaIcon icon={Sparkles} size={14} /></Link>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function FeaturedSignal({ event, mode }: { event: CHIEvent; mode: CanvasKey }) {
  const conceptFallback = mode === "network-atlas" ? "/media/events/salon-networking.webp" : mode === "program-ledger" ? "/media/events/featured-session-stage.webp" : "/media/events/executive-dinner.webp";
  const image = event.portraitImage || event.heroImage || conceptFallback;
  return (
    <article className={`featured-session featured-session--${mode}`}>
      <div className="featured-session__visual">
        <img src={image} alt="" aria-hidden="true" />
        <div className="featured-session__veil" />
        <span className="featured-session__label"><Sparkles size={15} aria-hidden="true" /> Featured session</span>
        <DateStamp event={event} variant="atlas" />
      </div>
      <div className="featured-session__copy">
        <p className="featured-session__kicker">{eventTopic(event)} · {eventFormat(event)}</p>
        <h2>{event.title}</h2>
        <p className="featured-session__description">{event.description}</p>
        <EventMeta event={event} />
        <div className="featured-session__footer">
          <span><CheckCircle2 size={16} aria-hidden="true" /> {event.seats - event.registered} seats currently open</span>
          <Link href={`/events/${event.slug}`} className="featured-session__cta">Review the session <CtaIcon icon={CheckCircle2} size={14} /></Link>
        </div>
      </div>
    </article>
  );
}

function CanvasSwitcher({ active }: { active: CanvasKey }) {
  return (
    <nav className="canvas-switcher" aria-label="Event-discovery concept canvases">
      <span className="canvas-switcher__label">Review 3 directions</span>
      <div>
        {CANVASES.map((canvas) => (
          <Link key={canvas.key} href={`/event-concepts/${canvas.key}`} className={canvas.key === active ? "is-active" : ""}>
            <span>{canvas.number}</span> {canvas.name}
          </Link>
        ))}
      </div>
    </nav>
  );
}

function SignalRoomCanvas({ events, filters }: { events: CHIEvent[]; filters: FilterControlsProps }) {
  return (
    <section className="event-canvas event-canvas--signal">
      <div className="event-canvas__topline"><span>CHI Alliance / Event Discovery</span><span>Concept 01 — Signal Room</span></div>
      <div className="signal-intro">
        <div>
          <p className="canvas-eyebrow">A more intentional calendar</p>
          <h1>The rooms that move <em>work</em> forward.</h1>
        </div>
        <p>One priority conversation, then a clear view of what the community is building next. This direction prioritizes editorial narrative over dashboard density.</p>
      </div>
      <FeaturedSignal event={FEATURED_EVENT} mode="signal-room" />
      <div className="signal-discovery">
        <aside><FilterControls {...filters} /></aside>
        <section className="signal-program">
          <div className="section-heading"><div><p>Full program</p><h2>Find your next room.</h2></div><Link href="/calendar-of-events">Open live calendar <CtaIcon icon={CalendarDays} size={14} /></Link></div>
          <ProgramList events={events} />
        </section>
      </div>
    </section>
  );
}

function ProgramLedgerCanvas({ events, filters }: { events: CHIEvent[]; filters: FilterControlsProps }) {
  return (
    <section className="event-canvas event-canvas--ledger">
      <div className="event-canvas__topline"><span>CHI Alliance / Program Office</span><span>Concept 02 — Program Ledger</span></div>
      <header className="ledger-intro">
        <div><p className="canvas-eyebrow">Executive program desk</p><h1>Every room, <em>accounted for.</em></h1></div>
        <div className="ledger-intro__metrics"><span><strong>{EVENTS.length}</strong> upcoming sessions</span><span><strong>3</strong> ways to refine</span><span><strong>{FEATURED_EVENT.seats - FEATURED_EVENT.registered}</strong> featured seats open</span></div>
      </header>
      <div className="ledger-feature-wrap"><FeaturedSignal event={FEATURED_EVENT} mode="program-ledger" /></div>
      <FilterControls {...filters} compact />
      <section className="ledger-program">
        <div className="ledger-program__head"><div><p>Program inventory</p><h2>A calendar with operational clarity.</h2></div><span>Showing {events.length} of {EVENTS.length}</span></div>
        <div className="ledger-legend"><span>Ref.</span><span>Date</span><span>Session</span><span>Speaker</span><span>Format & location</span><span aria-hidden="true" /></div>
        <LedgerList events={events} />
      </section>
    </section>
  );
}

function NetworkAtlasCanvas({ events, filters }: { events: CHIEvent[]; filters: FilterControlsProps }) {
  const meshellEvent = EVENTS.find((event) => event.slug === "the-secret-to-everyday-confidence");
  const spotlightImage = meshellEvent?.portraitImage || meshellEvent?.heroImage || "/media/events/salon-networking.webp";

  return (
    <section className="event-canvas event-canvas--atlas">
      <div className="event-canvas__topline"><span>CHI Alliance / Network Atlas</span><span>Concept 03 — Network Atlas</span></div>
      <div className="atlas-intro">
        <div><p className="canvas-eyebrow">Explore by relevance</p><h1>A living map of <em>who meets next.</em></h1><p>Built for the way people actually choose a session: a compelling person, a relevant topic, or a room they want to be in.</p></div>
        <div className="atlas-intro__image atlas-intro__image--speaker">
          <div className="atlas-intro__portrait-frame">
            <img src={spotlightImage} alt="Meshell R. Baker, Chief Confidence Igniter" />
          </div>
          <span>Meshell R. Baker <small>Chief Confidence Igniter</small></span>
        </div>
      </div>
      <div className="atlas-feature-area"><FeaturedSignal event={FEATURED_EVENT} mode="network-atlas" /></div>
      <div className="atlas-discovery">
        <FilterControls {...filters} />
        <div className="atlas-results-head"><div><p>Explore the network</p><h2>Choose your next conversation.</h2></div><span>{events.length} live paths</span></div>
        <AtlasGrid events={events} />
      </div>
    </section>
  );
}

export default function EventDiscoveryCanvases() {
  const [pathname] = useLocation();
  const canvas = canvasFromPath(pathname);
  const [locationFilter, setLocationFilter] = useState("all");
  const [topicFilter, setTopicFilter] = useState("all");
  const [speakerFilter, setSpeakerFilter] = useState("all");

  const filteredEvents = useMemo(() => EVENTS.filter((event) => {
    const matchesLocation = locationFilter === "all" || locationGroup(event) === locationFilter;
    const matchesTopic = topicFilter === "all" || eventTopic(event) === topicFilter;
    const matchesSpeaker = speakerFilter === "all" || event.speaker === speakerFilter;
    return matchesLocation && matchesTopic && matchesSpeaker;
  }), [locationFilter, topicFilter, speakerFilter]);

  const clearFilters = () => {
    setLocationFilter("all");
    setTopicFilter("all");
    setSpeakerFilter("all");
  };

  const filters: FilterControlsProps = {
    locationFilter,
    setLocationFilter,
    topicFilter,
    setTopicFilter,
    speakerFilter,
    setSpeakerFilter,
    onClear: clearFilters,
    resultCount: filteredEvents.length,
  };

  return (
    <div className="event-canvas-page">
      <Navigation />
      <main>
        <CanvasSwitcher active={canvas} />
        {canvas === "program-ledger" ? <ProgramLedgerCanvas events={filteredEvents} filters={filters} /> : canvas === "network-atlas" ? <NetworkAtlasCanvas events={filteredEvents} filters={filters} /> : <SignalRoomCanvas events={filteredEvents} filters={filters} />}
      </main>
      <Footer />
      <style>{`
        /* Brand-guide review canvases: high-contrast black + deep evergreen, leaf green for selection, gold for featured priority. */
        .event-canvas-page { min-height:100vh; color:#fff; background:#000; font-family:'Inter',sans-serif; }
        .event-canvas-page main { overflow:hidden; background:radial-gradient(64rem 38rem at 88% -8%,rgba(61,182,88,.19),transparent 62%),radial-gradient(50rem 34rem at 1% 30%,rgba(233,30,140,.22),transparent 65%),radial-gradient(38rem 24rem at 60% 60%,rgba(216,59,148,.08),transparent 72%),#000; }
        .canvas-switcher { position:relative; z-index:3; display:flex; align-items:center; justify-content:space-between; gap:1rem; max-width:78rem; margin:0 auto; padding:7.4rem 2rem 1.25rem; border-bottom:1px solid rgba(255,255,255,.14); }
        .canvas-switcher__label { color:rgba(255,255,255,.55); font-family:'Space Grotesk',sans-serif; font-size:.68rem; font-weight:700; letter-spacing:.14em; text-transform:uppercase; white-space:nowrap; }
        .canvas-switcher > div { display:flex; flex-wrap:wrap; justify-content:flex-end; gap:.4rem; }
        .canvas-switcher a { display:inline-flex; align-items:center; gap:.4rem; padding:.5rem .72rem; border:1px solid transparent; color:rgba(255,255,255,.62); font-family:'Space Grotesk',sans-serif; font-size:.72rem; font-weight:700; letter-spacing:.02em; text-decoration:none; transition:color 180ms ease,background 180ms ease,border-color 180ms ease,transform 180ms ease; }
        .canvas-switcher a span { color:#E91E8C; font-size:.62rem; }
        .canvas-switcher a:hover,.canvas-switcher a:focus-visible,.canvas-switcher a.is-active { border-color:rgba(233,30,140,.72); color:#fff; background:linear-gradient(105deg,rgba(233,30,140,.19),rgba(61,182,88,.08)); box-shadow:0 0 20px rgba(233,30,140,.13); outline:none; }
        .canvas-switcher a:active { transform:scale(.97); }
        .event-canvas { position:relative; isolation:isolate; max-width:78rem; margin:0 auto; padding:3.75rem 2rem 7rem; }
        .event-canvas::before { content:''; position:absolute; z-index:-1; inset:1rem -12rem 0; opacity:.56; pointer-events:none; background-image:radial-gradient(circle at 13% 16%,rgba(233,30,140,.46) 0 1px,transparent 1.5px),radial-gradient(circle at 77% 11%,rgba(61,182,88,.45) 0 1px,transparent 1.5px),radial-gradient(circle at 66% 55%,rgba(219,165,20,.38) 0 1px,transparent 1.5px),linear-gradient(121deg,transparent 18%,rgba(233,30,140,.13) 18.1%,transparent 18.5%,transparent 52%,rgba(61,182,88,.14) 52.1%,transparent 52.5%); background-size:170px 170px,230px 230px,300px 300px,100% 100%; mask-image:linear-gradient(to bottom,rgba(0,0,0,.94),transparent 88%); }
        .event-canvas::after { content:''; position:absolute; z-index:-1; width:32rem; height:32rem; top:22rem; right:-19rem; border-radius:50%; pointer-events:none; background:radial-gradient(circle,rgba(233,30,140,.22),rgba(61,182,88,.07) 36%,transparent 69%); filter:blur(8px); }
        .event-canvas__topline { display:flex; justify-content:space-between; gap:1rem; margin-bottom:2.35rem; color:rgba(255,255,255,.5); font-family:'Space Grotesk',sans-serif; font-size:.65rem; font-weight:700; letter-spacing:.12em; text-transform:uppercase; }
        .canvas-eyebrow { margin:0 0 .75rem; color:#3DB658; font-family:'Space Grotesk',sans-serif; font-size:.68rem; font-weight:800; letter-spacing:.14em; text-transform:uppercase; }
        .event-canvas h1,.event-canvas h2,.event-canvas h3 { margin:0; color:#fff; font-family:'Syne','Space Grotesk',sans-serif; font-weight:700; letter-spacing:-.07em; }
        .event-canvas h1 { max-width:42rem; font-size:clamp(3.35rem,7.5vw,6.8rem); line-height:.86; }
        .event-canvas h1 em { color:#E91E8C; font-style:normal; text-shadow:0 0 32px rgba(233,30,140,.22); }
        .event-canvas h2 { font-size:clamp(2rem,3.8vw,3.6rem); line-height:.98; }
        .event-canvas a:focus-visible,.event-canvas button:focus-visible,.event-canvas select:focus-visible { outline:2px solid #DBA514; outline-offset:3px; }
        .signal-intro { display:grid; grid-template-columns:minmax(0,1.1fr) minmax(16rem,.62fr); gap:3rem; align-items:end; margin-bottom:3rem; }
        .signal-intro > p { max-width:23rem; margin:0 0 .35rem; color:#B8C5BD; font-size:1rem; line-height:1.75; }
        .featured-session { display:grid; grid-template-columns:minmax(17rem,.88fr) minmax(0,1.12fr); min-height:31rem; overflow:hidden; border:1px solid rgba(219,165,20,.46); background:radial-gradient(circle at 79% 11%,rgba(233,30,140,.22),transparent 34%),radial-gradient(circle at 12% 87%,rgba(61,182,88,.25),transparent 46%),linear-gradient(128deg,rgba(32,80,60,.91),rgba(0,0,0,.96) 66%); box-shadow:0 34px 100px rgba(0,0,0,.45),0 0 0 1px rgba(233,30,140,.08),inset 0 1px rgba(255,255,255,.12); backdrop-filter:blur(18px); }
        .featured-session__visual { position:relative; min-height:21rem; overflow:hidden; }
        .featured-session__visual img { width:100%; height:100%; object-fit:cover; filter:saturate(.82) contrast(1.05); }
        .featured-session__veil { position:absolute; inset:0; background:linear-gradient(90deg,rgba(0,0,0,.08),rgba(0,0,0,.52)),linear-gradient(0deg,rgba(0,0,0,.72),transparent 55%),radial-gradient(circle at 14% 19%,rgba(233,30,140,.28),transparent 30%); }
        .featured-session__label { position:absolute; top:1.2rem; left:1.2rem; display:inline-flex; align-items:center; gap:.4rem; padding:.45rem .68rem; color:#181006; background:#DBA514; font-family:'Space Grotesk',sans-serif; font-size:.64rem; font-weight:800; letter-spacing:.1em; text-transform:uppercase; }
        .featured-session .event-date-stamp--atlas { position:absolute; right:1.2rem; bottom:1.2rem; }
        .featured-session__copy { display:flex; flex-direction:column; padding:clamp(1.7rem,4vw,3.65rem); background:linear-gradient(110deg,rgba(255,255,255,.055),transparent 52%); }
        .featured-session__kicker { margin:0 0 .8rem; color:#3DB658; font-family:'Space Grotesk',sans-serif; font-size:.69rem; font-weight:800; letter-spacing:.12em; text-transform:uppercase; }
        .featured-session h2 { max-width:38rem; font-size:clamp(2.25rem,4.8vw,4.35rem); line-height:.94; }
        .featured-session__description { max-width:35rem; margin:1.35rem 0 1.5rem; color:#C6D1C8; font-size:.92rem; line-height:1.68; }
        .canvas-event-meta { display:flex; flex-wrap:wrap; gap:.55rem 1rem; color:#B7C5BA; font-family:'Space Grotesk',sans-serif; font-size:.74rem; line-height:1.4; }
        .canvas-event-meta span { display:inline-flex; align-items:center; gap:.38rem; }
        .canvas-event-meta svg { color:#3DB658; }
        .featured-session__footer { display:flex; align-items:center; justify-content:space-between; gap:1rem; margin-top:auto; padding-top:1.5rem; border-top:1px solid rgba(255,255,255,.13); }
        .featured-session__footer > span { display:inline-flex; align-items:center; gap:.4rem; color:#D3DDD5; font-family:'Space Grotesk',sans-serif; font-size:.7rem; font-weight:600; }
        .featured-session__footer > span svg { color:#3DB658; }
        .featured-session__cta { display:inline-flex; align-items:center; gap:.55rem; padding:.78rem .92rem; color:#fff; background:#E91E8C; font-family:'Space Grotesk',sans-serif; font-size:.72rem; font-weight:800; letter-spacing:.04em; text-decoration:none; transition:transform 180ms ease,filter 180ms ease,box-shadow 180ms ease; }
        .featured-session__cta:hover { filter:brightness(1.08); box-shadow:0 12px 25px rgba(233,30,140,.32); transform:translateY(-2px); }
        .featured-session__cta:active { transform:scale(.97); }
        .signal-discovery { display:grid; grid-template-columns:minmax(14rem,.36fr) minmax(0,1fr); gap:3rem; margin-top:4rem; }
        .canvas-filter-panel { position:relative; overflow:hidden; padding:1.25rem; border:1px solid rgba(255,255,255,.18); background:linear-gradient(135deg,rgba(255,255,255,.095),rgba(9,20,15,.83) 38%,rgba(233,30,140,.075)); box-shadow:0 16px 42px rgba(0,0,0,.21),inset 0 1px rgba(255,255,255,.09); backdrop-filter:blur(20px); }
        .canvas-filter-panel::before { content:''; position:absolute; width:8rem; height:8rem; top:-5rem; right:-3rem; border-radius:50%; pointer-events:none; background:radial-gradient(circle,rgba(233,30,140,.27),transparent 70%); }
        .canvas-filter-panel__intro { display:flex; align-items:start; justify-content:space-between; gap:1rem; margin-bottom:1.15rem; }
        .canvas-filter-panel__eyebrow { display:inline-flex; align-items:center; gap:.4rem; color:#3DB658; font-family:'Space Grotesk',sans-serif; font-size:.64rem; font-weight:800; letter-spacing:.11em; text-transform:uppercase; }
        .canvas-filter-panel__intro p { margin:0; color:rgba(255,255,255,.58); font-family:'Space Grotesk',sans-serif; font-size:.66rem; white-space:nowrap; }
        .canvas-filter-panel__controls { display:grid; gap:.8rem; }
        .canvas-filter-panel label { display:grid; gap:.38rem; color:#C7D3C9; font-family:'Space Grotesk',sans-serif; font-size:.64rem; font-weight:700; letter-spacing:.05em; }
        .canvas-select-shell { position:relative; }
        .canvas-select-shell select { width:100%; appearance:none; min-height:2.6rem; padding:.55rem 2.1rem .55rem .72rem; border:1px solid rgba(255,255,255,.16); border-radius:0; color:#fff; background:rgba(0,0,0,.46); font-family:'Inter',sans-serif; font-size:.78rem; }
        .canvas-select-shell svg { position:absolute; top:50%; right:.62rem; color:#3DB658; pointer-events:none; transform:translateY(-50%); }
        .canvas-filter-panel__clear { display:inline-flex; align-items:center; gap:.35rem; margin-top:1rem; padding:0; border:0; color:#E4B1CC; background:transparent; font-family:'Space Grotesk',sans-serif; font-size:.68rem; font-weight:700; cursor:pointer; transition:color 180ms ease,opacity 180ms ease; }
        .canvas-filter-panel__clear:hover { color:#D83B94; }
        .canvas-filter-panel__clear:disabled { opacity:.35; cursor:not-allowed; }
        .signal-program { min-width:0; }
        .section-heading { display:flex; align-items:end; justify-content:space-between; gap:1rem; margin-bottom:1.35rem; }
        .section-heading p,.ledger-program__head p,.atlas-results-head p { margin:0 0 .45rem; color:#DBA514; font-family:'Space Grotesk',sans-serif; font-size:.66rem; font-weight:800; letter-spacing:.13em; text-transform:uppercase; }
        .section-heading h2 { font-size:clamp(1.9rem,3.4vw,3.15rem); }
        .section-heading a { display:inline-flex; align-items:center; gap:.42rem; color:#E5EEE6; font-family:'Space Grotesk',sans-serif; font-size:.72rem; font-weight:700; text-decoration:none; white-space:nowrap; }
        .section-heading a:hover { color:#3DB658; }
        .signal-program-list { display:grid; gap:.68rem; }
        .signal-program-card { display:grid; grid-template-columns:4.4rem minmax(0,1fr) auto; align-items:center; gap:1rem; padding:1.05rem; border:1px solid rgba(255,255,255,.16); border-left:2px solid var(--event-tone); background:linear-gradient(112deg,rgba(255,255,255,.09),rgba(255,255,255,.028) 44%,rgba(0,0,0,.2)); box-shadow:inset 0 1px rgba(255,255,255,.05); backdrop-filter:blur(14px); transition:background 180ms ease,border-color 180ms ease,box-shadow 180ms ease,transform 180ms ease; }
        .signal-program-card:hover { border-color:var(--event-tone); background:linear-gradient(100deg,color-mix(in srgb,var(--event-tone) 15%,rgba(255,255,255,.09)),rgba(233,30,140,.06)); box-shadow:0 12px 28px color-mix(in srgb,var(--event-tone) 16%,transparent); transform:translateX(3px); }
        .event-date-stamp { display:flex; flex-direction:column; align-items:center; justify-content:center; width:4rem; min-height:4.25rem; border:1px solid color-mix(in srgb,var(--event-tone,#DBA514) 62%,rgba(255,255,255,.1)); background:rgba(0,0,0,.46); font-family:'Space Grotesk',sans-serif; }
        .event-date-stamp span { color:#D0DDD1; font-size:.58rem; font-weight:800; letter-spacing:.12em; text-transform:uppercase; }
        .event-date-stamp strong { color:#fff; font-family:'Syne','Space Grotesk',sans-serif; font-size:1.42rem; line-height:1.1; letter-spacing:-.07em; }
        .signal-program-card__labels { display:flex; flex-wrap:wrap; gap:.4rem; margin-bottom:.45rem; }
        .signal-program-card__labels span { color:var(--event-tone); font-family:'Space Grotesk',sans-serif; font-size:.6rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; }
        .signal-program-card__labels span + span { color:#B9C7BB; }
        .signal-program-card h3 { font-size:clamp(1.25rem,2.2vw,1.65rem); line-height:1.03; }
        .signal-program-card .canvas-event-meta { margin-top:.58rem; }
        .signal-program-card__link { display:inline-flex; align-items:center; gap:.4rem; padding:.6rem .25rem; color:#E2EDE4; font-family:'Space Grotesk',sans-serif; font-size:.68rem; font-weight:700; text-decoration:none; white-space:nowrap; }
        .signal-program-card__link:hover { color:#3DB658; }
        .canvas-empty-state { display:grid; place-items:center; gap:.65rem; min-height:14rem; padding:2rem; border:1px dashed rgba(61,182,88,.55); color:#C4D4C7; text-align:center; background:rgba(61,182,88,.055); }
        .canvas-empty-state svg { color:#3DB658; }
        .canvas-empty-state strong { font-family:'Space Grotesk',sans-serif; font-size:.92rem; }
        .canvas-empty-state p { max-width:23rem; margin:0; color:#9BAC9E; font-size:.8rem; line-height:1.55; }

        /* Concept 02 — precision ledger */
        .event-canvas--ledger { padding-top:3.15rem; }
        .ledger-intro { display:flex; justify-content:space-between; align-items:end; gap:2rem; margin-bottom:2.5rem; }
        .ledger-intro h1 { max-width:38rem; }
        .ledger-intro__metrics { display:flex; flex-wrap:wrap; justify-content:flex-end; gap:.5rem; max-width:25rem; }
        .ledger-intro__metrics span { padding:.62rem .75rem; border:1px solid rgba(255,255,255,.15); color:#C9D8CC; background:rgba(32,80,60,.35); font-family:'Space Grotesk',sans-serif; font-size:.65rem; line-height:1.35; }
        .ledger-intro__metrics strong { color:#DBA514; font-size:.88rem; }
        .ledger-feature-wrap { margin-bottom:1.1rem; }
        .featured-session--program-ledger { grid-template-columns:minmax(15rem,.58fr) minmax(0,1.42fr); min-height:22.5rem; border-color:rgba(61,182,88,.5); background:linear-gradient(110deg,#0D2A20,rgba(0,0,0,.97)); }
        .featured-session--program-ledger h2 { max-width:44rem; font-size:clamp(2rem,3.8vw,3.55rem); }
        .featured-session--program-ledger .featured-session__description { display:-webkit-box; overflow:hidden; -webkit-box-orient:vertical; -webkit-line-clamp:2; }
        .canvas-filter-panel--compact { display:flex; align-items:end; gap:1.2rem; padding:1rem 1.1rem; margin:0 0 2.7rem; background:rgba(7,16,11,.92); }
        .canvas-filter-panel--compact .canvas-filter-panel__intro { display:block; min-width:10.5rem; margin:0; }
        .canvas-filter-panel--compact .canvas-filter-panel__intro p { margin-top:.35rem; }
        .canvas-filter-panel--compact .canvas-filter-panel__controls { grid-template-columns:repeat(3,minmax(0,1fr)); flex:1; }
        .canvas-filter-panel--compact .canvas-filter-panel__clear { margin:0 0 .62rem; white-space:nowrap; }
        .ledger-program { border-top:1px solid rgba(255,255,255,.17); }
        .ledger-program__head { display:flex; align-items:end; justify-content:space-between; gap:1rem; padding:2rem 0 1.35rem; }
        .ledger-program__head h2 { font-size:clamp(1.85rem,3.4vw,3.05rem); }
        .ledger-program__head > span { color:#AFC2B4; font-family:'Space Grotesk',sans-serif; font-size:.72rem; }
        .ledger-legend,.ledger-row { display:grid; grid-template-columns:2.75rem 4.5rem minmax(13rem,1.7fr) minmax(8rem,.8fr) minmax(7.5rem,.7fr) 2.5rem; gap:1rem; align-items:center; }
        .ledger-legend { padding:0 .8rem .62rem; color:rgba(255,255,255,.43); font-family:'Space Grotesk',sans-serif; font-size:.59rem; font-weight:700; letter-spacing:.09em; text-transform:uppercase; }
        .ledger-list { border-top:1px solid rgba(255,255,255,.14); }
        .ledger-row { min-height:7.4rem; padding:.8rem; border-bottom:1px solid rgba(255,255,255,.14); background:rgba(255,255,255,.02); transition:background 180ms ease,transform 180ms ease; }
        .ledger-row:hover { background:linear-gradient(90deg,color-mix(in srgb,var(--event-tone) 10%,rgba(255,255,255,.035)),transparent); transform:translateX(3px); }
        .ledger-row__index { color:var(--event-tone); font-family:'Space Grotesk',sans-serif; font-size:.65rem; font-weight:800; }
        .event-date-stamp--ledger { width:3.7rem; min-height:3.85rem; }
        .event-date-stamp--ledger strong { font-size:1.4rem; }
        .ledger-row__session p { margin:0 0 .32rem; color:var(--event-tone); font-family:'Space Grotesk',sans-serif; font-size:.59rem; font-weight:800; letter-spacing:.1em; text-transform:uppercase; }
        .ledger-row__session h3 { font-size:1.28rem; line-height:1.02; }
        .ledger-row__session > span,.ledger-row__speaker span,.ledger-row__where span { display:block; margin-top:.34rem; color:#95A99A; font-size:.71rem; line-height:1.35; }
        .ledger-row__speaker,.ledger-row__where { color:#E0EAE2; font-family:'Space Grotesk',sans-serif; font-size:.73rem; }
        .ledger-row__speaker strong,.ledger-row__where strong { display:block; margin-top:.22rem; font-size:.79rem; line-height:1.25; }
        .ledger-row__link { display:grid; place-items:center; width:2.15rem; height:2.15rem; border:1px solid rgba(61,182,88,.65); color:#3DB658; text-decoration:none; transition:background 180ms ease,color 180ms ease,transform 180ms ease; }
        .ledger-row__link:hover { color:#07100A; background:#3DB658; transform:translateX(2px); }

        /* Concept 03 — relationship atlas */
        .event-canvas--atlas { padding-top:3.15rem; }
        .atlas-intro { display:grid; grid-template-columns:minmax(0,1fr) minmax(19rem,.65fr); gap:3.5rem; align-items:end; margin-bottom:3rem; }
        .atlas-intro > div > p:not(.canvas-eyebrow) { max-width:34rem; margin:1.35rem 0 0; color:#B6C5B9; font-size:1rem; line-height:1.72; }
        .atlas-intro__image { position:relative; min-height:14rem; overflow:hidden; border:1px solid rgba(216,59,148,.48); }
        .atlas-intro__image img { width:100%; height:100%; object-fit:cover; filter:saturate(.78) contrast(1.05); }
        .atlas-intro__image--speaker { display:grid; place-items:center; gap:1.05rem; min-height:22rem; padding:1.35rem 1.5rem 1.2rem; overflow:visible; border-color:rgba(233,30,140,.62); background:radial-gradient(circle at 50% 38%,rgba(233,30,140,.24),transparent 45%),radial-gradient(circle at 84% 16%,rgba(219,165,20,.18),transparent 34%),linear-gradient(145deg,rgba(25,8,21,.98),rgba(4,8,7,.98)); box-shadow:0 22px 48px rgba(0,0,0,.28),inset 0 1px rgba(255,255,255,.09); }
        .atlas-intro__portrait-frame { position:relative; z-index:1; width:min(16.5rem,65vw); aspect-ratio:1; padding:4px; border-radius:50%; background:conic-gradient(from 132deg,#E91E8C,#DBA514 33%,#3DB658 57%,#E91E8C 84%,#DBA514); box-shadow:0 0 0 1px rgba(255,255,255,.18),0 0 36px rgba(233,30,140,.24),0 18px 40px rgba(0,0,0,.46); }
        .atlas-intro__portrait-frame::after { content:''; position:absolute; inset:8px; border:1px solid rgba(255,255,255,.3); border-radius:50%; pointer-events:none; }
        .atlas-intro__image--speaker img { display:block; width:100%; height:100%; border-radius:50%; object-fit:cover; object-position:center 20%; filter:saturate(.96) contrast(1.04); }
        .atlas-intro__image::after { content:''; position:absolute; inset:0; background:linear-gradient(110deg,rgba(0,0,0,.02),rgba(0,0,0,.7)); }
        .atlas-intro__image span { position:absolute; z-index:1; right:1rem; bottom:1rem; max-width:8rem; color:#fff; font-family:'Syne','Space Grotesk',sans-serif; font-size:1.02rem; font-weight:700; line-height:1.05; letter-spacing:-.06em; text-align:right; }
        .atlas-intro__image--speaker::after { inset:auto 1.25rem 1.05rem; height:1px; background:linear-gradient(90deg,transparent,rgba(219,165,20,.72),transparent); }
        .atlas-intro__image--speaker span { position:relative; right:auto; bottom:auto; max-width:100%; color:#fff; font-size:.96rem; line-height:1.06; text-align:center; text-shadow:0 3px 16px rgba(0,0,0,.62); }
        .atlas-intro__image--speaker span small { display:block; margin-top:.34rem; color:#DBA514; font-family:'Space Grotesk',sans-serif; font-size:.58rem; font-weight:800; letter-spacing:.14em; text-transform:uppercase; }
        .atlas-feature-area { position:relative; margin-bottom:2.7rem; }
        .featured-session--network-atlas { grid-template-columns:minmax(0,1.1fr) minmax(17rem,.9fr); min-height:25.5rem; border-color:rgba(216,59,148,.55); background:linear-gradient(133deg,#160C15,rgba(0,0,0,.96) 68%); }
        .featured-session--network-atlas .featured-session__visual { order:2; }
        .featured-session--network-atlas h2 { font-size:clamp(2.1rem,4.1vw,3.7rem); }
        .featured-session--network-atlas .featured-session__cta { background:#E91E8C; color:#fff; }
        .atlas-discovery .canvas-filter-panel { display:grid; grid-template-columns:13rem minmax(0,1fr) auto; align-items:end; gap:1.4rem; margin-bottom:2.75rem; background:rgba(14,9,13,.84); border-color:rgba(216,59,148,.38); }
        .atlas-discovery .canvas-filter-panel__intro { display:block; margin:0; }
        .atlas-discovery .canvas-filter-panel__intro p { margin-top:.35rem; }
        .atlas-discovery .canvas-filter-panel__controls { grid-template-columns:repeat(3,minmax(0,1fr)); }
        .atlas-discovery .canvas-filter-panel__clear { margin:0 0 .62rem; white-space:nowrap; }
        .atlas-results-head { display:flex; align-items:end; justify-content:space-between; gap:1rem; margin-bottom:1.35rem; }
        .atlas-results-head h2 { font-size:clamp(1.9rem,3.4vw,3.1rem); }
        .atlas-results-head > span { color:#DEA1C7; font-family:'Space Grotesk',sans-serif; font-size:.72rem; }
        .atlas-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:1rem; }
        .atlas-card { overflow:hidden; border:1px solid rgba(255,255,255,.17); background:linear-gradient(138deg,rgba(255,255,255,.09),rgba(255,255,255,.025) 52%,rgba(233,30,140,.04)); box-shadow:inset 0 1px rgba(255,255,255,.06); backdrop-filter:blur(14px); transition:transform 190ms ease,border-color 190ms ease,box-shadow 190ms ease; }
        .atlas-card:hover { border-color:var(--event-tone); box-shadow:0 20px 44px color-mix(in srgb,var(--event-tone) 16%,transparent); transform:translateY(-5px); }
        .atlas-card__image { position:relative; height:12.5rem; overflow:hidden; }
        .atlas-card__image img { width:100%; height:100%; object-fit:cover; filter:saturate(.68) contrast(1.05); transition:transform 280ms ease; }
        .atlas-card:hover .atlas-card__image img { transform:scale(1.055); }
        .atlas-card__image-veil { position:absolute; inset:0; background:linear-gradient(0deg,rgba(0,0,0,.7),transparent 62%); }
        .event-date-stamp--atlas { position:absolute; right:.85rem; bottom:.85rem; width:3.5rem; min-height:3.7rem; background:rgba(0,0,0,.72); backdrop-filter:blur(8px); }
        .event-date-stamp--atlas strong { font-size:1.35rem; }
        .atlas-card__topic { position:absolute; top:.85rem; left:.85rem; max-width:calc(100% - 6rem); padding:.38rem .52rem; color:#fff; border:1px solid color-mix(in srgb,var(--event-tone) 72%,rgba(255,255,255,.2)); background:rgba(0,0,0,.56); font-family:'Space Grotesk',sans-serif; font-size:.58rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; }
        .atlas-card__body { display:flex; flex-direction:column; min-height:14rem; padding:1.1rem; }
        .atlas-card__body > p { margin:0 0 .55rem; color:var(--event-tone); font-family:'Space Grotesk',sans-serif; font-size:.62rem; font-weight:800; letter-spacing:.09em; text-transform:uppercase; }
        .atlas-card h3 { font-size:clamp(1.45rem,2.6vw,2rem); line-height:.98; }
        .atlas-card__speaker { display:flex; align-items:center; gap:.4rem; margin-top:1rem; color:#BCCABC; font-family:'Space Grotesk',sans-serif; font-size:.72rem; }
        .atlas-card__speaker svg { color:var(--event-tone); }
        .atlas-card__link { display:inline-flex; align-items:center; gap:.45rem; margin-top:auto; padding-top:1rem; color:#fff; font-family:'Space Grotesk',sans-serif; font-size:.7rem; font-weight:800; text-decoration:none; }
        .atlas-card__link:hover { color:var(--event-tone); }
        @media (max-width:980px) { .signal-discovery { grid-template-columns:1fr; gap:1.5rem; } .canvas-filter-panel__controls { grid-template-columns:repeat(3,minmax(0,1fr)); } .canvas-filter-panel--compact { display:grid; grid-template-columns:1fr auto; } .canvas-filter-panel--compact .canvas-filter-panel__intro { min-width:0; } .canvas-filter-panel--compact .canvas-filter-panel__controls { grid-column:1 / -1; } .ledger-legend { display:none; } .ledger-row { grid-template-columns:2.25rem 4.25rem minmax(0,1fr) auto; } .ledger-row__speaker,.ledger-row__where { display:none; } .atlas-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } .atlas-discovery .canvas-filter-panel { grid-template-columns:1fr auto; } .atlas-discovery .canvas-filter-panel__controls { grid-column:1 / -1; } }
        @media (max-width:720px) { .canvas-switcher { align-items:flex-start; flex-direction:column; padding:6.5rem 1rem 1rem; } .canvas-switcher > div { justify-content:flex-start; } .event-canvas { padding:2.5rem 1rem 4.5rem; } .event-canvas__topline { margin-bottom:1.55rem; font-size:.56rem; } .event-canvas__topline span:last-child { display:none; } .signal-intro,.ledger-intro,.atlas-intro { grid-template-columns:1fr; gap:1.5rem; margin-bottom:2.25rem; } .event-canvas h1 { font-size:clamp(3rem,15vw,4.8rem); } .ledger-intro__metrics { justify-content:flex-start; } .featured-session,.featured-session--program-ledger,.featured-session--network-atlas { grid-template-columns:1fr; min-height:0; } .featured-session--network-atlas .featured-session__visual { order:0; } .featured-session__visual { min-height:15rem; } .featured-session__copy { padding:1.4rem; } .featured-session h2 { font-size:clamp(2.2rem,10vw,3.4rem); } .featured-session__description { font-size:.86rem; } .featured-session__footer { align-items:flex-start; flex-direction:column; } .section-heading,.ledger-program__head,.atlas-results-head { align-items:flex-start; flex-direction:column; } .canvas-filter-panel__controls,.canvas-filter-panel--compact .canvas-filter-panel__controls,.atlas-discovery .canvas-filter-panel__controls { grid-template-columns:1fr; } .canvas-filter-panel--compact,.atlas-discovery .canvas-filter-panel { display:grid; grid-template-columns:1fr auto; } .canvas-filter-panel--compact .canvas-filter-panel__controls,.atlas-discovery .canvas-filter-panel__controls { grid-column:1 / -1; } .signal-program-card { grid-template-columns:3.65rem minmax(0,1fr); gap:.8rem; } .signal-program-card__link { grid-column:2; justify-self:start; padding:.2rem 0; } .event-date-stamp { width:3.5rem; min-height:3.75rem; } .signal-program-card h3 { font-size:1.3rem; } .ledger-row { grid-template-columns:2rem 3.8rem minmax(0,1fr) 2rem; gap:.7rem; padding:.75rem 0; } .ledger-row__session h3 { font-size:1.16rem; } .ledger-row__session > span { display:none; } .atlas-grid { grid-template-columns:1fr; } .atlas-card__image { height:14rem; } }
        @media (prefers-reduced-motion:reduce) { .event-canvas-page *, .event-canvas-page *::before, .event-canvas-page *::after { scroll-behavior:auto !important; transition:none !important; } }
      `}</style>
    </div>
  );
}
